import {
  Filter
} from '../common/filter';

const updateFilter = f => {
  f.timeTo = null;
  f.timeFrom = f.timeTo;
  f.record_count = 0;
  Object.setPrototypeOf(f, Filter.prototype);
  f.typize();
  return f;
};

export class FilterResource {
  constructor(
    $http,
    $q,
    md5,
    $cookies,
    loadWithCache,
    $httpParamSerializer,
  ) {
    this.loadWithCache = loadWithCache;
    this.$http = $http;
    this.$q = $q;
    this.md5 = md5;
    this.$cookies = $cookies;
    this.$httpParamSerializer = $httpParamSerializer;
  }

  // Delete saved filter (ID should be set)
  delete(filter) {
    return this.$http.post('/analytics/filter/delete/', {
        id: filter.id
      }, {
        headers: {
          'Accept': 'application/json;charset=utf-8',
          'X-CSRFToken': this.$cookies.get('csrftoken'),
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      })
      .then(({
        data,
        status
      }) => {
        return {
          data,
          status
        };
      })
      .catch(({
        status
      }) => {
        throw new Error('HTTP error: ' + status);
      });
  }

  // Save filter to server, get ID
  save(filter) {
    const filterData = Object.assign({}, filter);
    filterData.content_type = filter.content_type;
    filterData.category = filter.category;
    if (!filter.isGraph) delete filterData.graphOptions;
    delete filterData.isNodeOpen;

    const promise = new Promise(resolve => {
      this.$http.post('/analytics/filter/save/', filterData, {
          headers: {
            'Accept': 'application/json;charset=utf-8',
            'X-CSRFToken': this.$cookies.get('csrftoken'),
            'Content-Type': 'application/json;charset=UTF-8',
          },
        })
        .then(({
          data,
          status
        }) => {
          filter.saved = true;
          filter.isChanged = false;
          filter.id = data.id;
          if (!filter.children || !filter.children.length) filter.children = data.children;
          else data.children = filter.children;

          resolve({
            status,
            data: filter,
            error: null
          });
        })
        .catch(({
          data,
          status
        }) => {
          resolve({
            status,
            data,
            error: Error('HTTP error: ' + status),
          });
        });
    });
    return promise;
  }

  saveFilters(filters) {
    let result = Promise.resolve();
    for (const f of filters) {
      result = result.then(res => this.save(f));
    }
    return result;
  }

  saveChildrens(filter, asNew = true) {
    let result = Promise.resolve();
    if (!filter.children || !filter.children.length) return result;
    for (const f of filter.children.reverse()) {
      result = result.then(res => this.saveChildren(f, filter.id, asNew));
    }
    return result;
  }

  saveChildren(filter, parentId, asNew = true) {
    if (asNew) {
      delete filter.id;
      delete filter.parent_id;
    }
    return new Promise((resolve, reject) => {
      if (asNew) {
        delete filter.id;
        delete filter.parent_id;
      }
      this.save(filter)
        .then(({
          status,
          data,
          error
        }) => {
          if (error) {
            reject(error);
          } else {
            return this.changePosition(data.id, null, parentId)
              .then(res => resolve(data))
              .catch(e => reject(e));
          }
        });
    });
  }


  // id: position.id,
  // prev_id: position.prev_id,
  // parent_id: position.parent_id,
  changePosition(id, prev_id, parent_id) {
    return this.$http.post('/analytics/filter/update_position/', {
      id,
      prev_id,
      parent_id
    }, {
      headers: {
        'Accept': 'application/json;charset=utf-8',
        'X-CSRFToken': this.$cookies.get('csrftoken'),
        'Content-Type': 'application/json;charset=UTF-8',
      },
    });
  }

  // Return list of all filters
  list(id) {
    const url = id === null || id === undefined ?
      '/analytics/filter/list/' : `/analytics/filter/list/?parentId=${id}`;
    return this.$http.get(url, {})
      .then(({
        data
      }) => {
        const result = data.map(updateFilter);
        return result;
      })
      .catch(err => {
        console.error(err);
        return [];
      });
  }

  listOther(filterIds, id) {
    return this.$http.post('/analytics/filter/list_other/', {
        loadedIds: filterIds ? filterIds : [],
        id: id
      }, {
        headers: {
          'Accept': 'application/json;charset=utf-8',
          'X-CSRFToken': this.$cookies.get('csrftoken'),
          'Content-Type': 'application/json;charset=UTF-8',
        },
      })
      .then(({
        data,
        status
      }) => {
        const result = data.map(updateFilter);
        return result;
      })
      .catch(err => {
        console.error(err);
        return [];
      });
  }

  search(searchTrim = '', id = null) {
    let url = `/analytics/filter/search/?searchTrim=${searchTrim}`;
    if (id != null) url = `${url}&id=${id}`;
    return this.$http.get(url, {})
      .then(({
        data
      }) => {
        return data;
      })
      .catch(({
        status
      }) => {
        throw new Error('HTTP error: ' + status);
      });
  }

  checkRegExps(regExps) {
    return new Promise(resolve => Promise.all(regExps.map(keyword => this.checkRegExp(keyword)))
      .then(res => {
        let err = null;
        if (Array.isArray(res)) {
          for (err of res) {
            if (err) break;
          }
        } else err = res;
        resolve(err);
      }));
  }

  checkRegExp(regExp) {
    return new Promise(resolve => {
      this.loadWithCache(`/analytics/check_regexp/?regexp=${encodeURIComponent(regExp)}`)
        .then(res => {
          let err = null;
          if (res.error) {
            err = `"${regExp}" - ${res.error}`;
          }
          resolve(err);
        });
    });
  }

  matchEventTextData(regExp) {
    return this.$http.get(`/analytics/match_event_text_data/?regexp=${regExp}`)
      .then(({
        data
      }) => {
        return data;
      })
      .catch(({
        status
      }) => {
        throw new Error('HTTP error: ' + status);
      });
  }

  // POST QueryString to separate handler with by md5 hash as key
  // This handler will store QueryString to cache
  // After that load frame with ?hash=XXXX param
  // This param handled by django middleware and change request.GET to cached value

  sendHash(src) {
    const filterSearch = search => {
      const notNeedParams = ['barDim', 'null', 'paneClose'];
      let first = '';
      if (search[0] == '?') {
        first = '?';
        search = search.substring(1);
      }
      const res = search.split('&').filter(s => {
        const param = s.split('=')[0];
        return !notNeedParams.some(p => p == param);
      }).join('&');
      return res ? `${first}${res}` : '';
    };

    return new Promise((resolve, reject) => {
      const parts = src.split('?');
      const url = parts[0];
      const body = filterSearch(parts[1]);
      if (body) {
        const hash = this.md5.createHash(body);
        const req_params = {};

        req_params['headers'] = {
          'X-CSRFToken': this.$cookies.get('csrftoken'),
          'Content-Type': 'application/x-www-form-urlencoded',
        };

        this.$http.post('/analytics/reg_body/?h=' + hash, body, req_params)
          .then(({
            data,
            status,
            headers,
            config
          }) => {
            resolve(url + '?hash=' + hash);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  getCompleteFact(filter, id) {
    if (!id) return Promise.resolve({});
    let params;
    if (filter.filters && filter.filters.length) {
      params = filter.buildQueryParams();
      params.complete_data = true;
      params.dim.push('id@default:id');
      params.flags.push('@');
      params.filter.push(id);
      params.order = 'local_time';
    } else {
      params = {
        dim: ['id@default:id'],
        flag: ['@'],
        filter: [id],
        complete_data: true,
        order: 'local_time',
        dir: 'desc',
      };
    }
    return this.$http.post('/analytics/facts/agent_event/', this.$httpParamSerializer(params), {
      headers: {
        'Accept': 'application/json;charset=utf-8',
        'X-CSRFToken': this.$cookies.get('csrftoken'),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }).then(({
      data
    }) => {
      if (data && data.object_list && data.object_list.length) return data.object_list[0];
      return {};
    });
  }
}



// WEBPACK FOOTER //
// ./src/core/filter-resource.service.js